import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, User, Mail, Phone, Car, Building2, ArrowRight, CheckCircle, Home as HomeIcon, Bike, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { submitQuoteRequest } from '../utils/api';
import { trackLead } from '../utils/pixel';
import { Seo } from '../components/Seo';

const Quote = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    insuranceType: '',
    message: ''
  });

  const insuranceTypes = [
    { value: 'auto', label: 'Auto Insurance', icon: Car },
    { value: 'home', label: 'Home Insurance', icon: HomeIcon },
    { value: 'business', label: 'Business Insurance', icon: Building2 },
    { value: 'motorcycle', label: 'Motorcycle Insurance', icon: Bike },
    { value: 'bonds', label: 'Contractor Bond', icon: Shield }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    // Validate name
    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name');
      return false;
    }
    if (formData.fullName.trim().length < 2) {
      toast.error('Please enter a valid name (at least 2 characters)');
      return false;
    }

    // Validate email
    if (!formData.email.trim()) {
      toast.error('Please enter your email address');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Validate phone
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return false;
    }
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number (at least 10 digits)');
      return false;
    }

    // Validate insurance type
    if (!formData.insuranceType) {
      toast.error('Please select an insurance type');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Transform form data to match API schema
      const apiData = {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        insurance_type: formData.insuranceType,
        message: formData.message
      };
      
      const response = await submitQuoteRequest(apiData);
      if (response.success) {
        trackLead('Quote Request');
        toast.success(response.message);
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          insuranceType: '',
          message: ''
        });
        // Navigate to home after short delay
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      const errorMessage = error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br bg-white">
      <Seo
        title="Get a Free Insurance Quote | Kerry M Holloway Agency - Prineville, OR"
        description="Request a free insurance quote from Kerry M Holloway Agency in Prineville, Oregon. Contractor bonds, business, home, auto, and motorcycle insurance for Central Oregon. Fast, honest answers."
        path="/quote"
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: "360px", paddingBottom: "80px" }}>
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center space-x-2 bg-red-100 px-4 py-2 rounded-full mb-4 sm:mb-6">
              <FileText className="h-4 w-4 text-red-800" />
              <span className="text-sm font-medium text-red-800">Free Quote Request</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F1F1F] mb-3 sm:mb-4">
              Get Your Free Insurance Quote
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Fill out the form below and we'll get back to you within 24 hours with a personalized quote tailored to your needs.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-xl border-2 border-gray-100">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} noValidate className="space-y-6">
                    {/* Full Name */}
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-[#1F1F1F] font-semibold">
                        Full Name *
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="fullName"
                          type="text"
                          placeholder="John Doe"
                          value={formData.fullName}
                          onChange={(e) => handleInputChange('fullName', e.target.value)}
                          className="pl-10 h-12 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A]"
                          required
                        />
                      </div>
                    </div>

                    {/* Email & Phone */}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#1F1F1F] font-semibold">
                          Email Address *
                        </Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <Input
                            id="email"
                            type="text"
                            placeholder="john@example.com"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="pl-10 h-12 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A]"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#1F1F1F] font-semibold">
                          Phone Number *
                        </Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="(555) 123-4567"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className="pl-10 h-12 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Insurance Type */}
                    <div className="space-y-2">
                      <Label htmlFor="insuranceType" className="text-[#1F1F1F] font-semibold">
                        Type of Insurance *
                      </Label>
                      <Select
                        value={formData.insuranceType}
                        onValueChange={(value) => handleInputChange('insuranceType', value)}
                      >
                        <SelectTrigger className="h-12 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A]">
                          <SelectValue placeholder="Select insurance type" />
                        </SelectTrigger>
                        <SelectContent>
                          {insuranceTypes.map((type) => {
                            const Icon = type.icon;
                            return (
                              <SelectItem key={type.value} value={type.value}>
                                <div className="flex items-center space-x-2">
                                  <Icon className="h-4 w-4 text-[#D94B5A]" />
                                  <span>{type.label}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#1F1F1F] font-semibold">
                        Additional Information (Optional)
                      </Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us more about your insurance needs..."
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        className="min-h-32 border-gray-300 focus:border-red-500 focus:ring-[#D94B5A] resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r bg-[#D94B5A] hover:bg-[#C43D4D] text-white shadow-lg hover:shadow-xl transition-all duration-300 group h-14 text-lg"
                    >
                      {isSubmitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          Request Free Quote
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-gray-600 text-center">
                      * Required fields. We'll never share your information with third parties.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* What to Expect */}
              <Card className="shadow-lg border-2 border-gray-100">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-[#1F1F1F]">What Happens Next?</h3>
                  
                  <div className="space-y-4">
                    {[
                      { step: '1', title: 'Review', desc: 'We review your information' },
                      { step: '2', title: 'Compare', desc: 'Compare rates from 20+ carriers' },
                      { step: '3', title: 'Contact', desc: 'We call you with the best options' },
                      { step: '4', title: 'Protect', desc: 'Get coverage that fits your needs' }
                    ].map((item) => (
                      <div key={item.step} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br bg-[#D94B5A] rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {item.step}
                        </div>
                        <div>
                          <div className="font-semibold text-[#1F1F1F]">{item.title}</div>
                          <div className="text-sm text-gray-600">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Why Choose Us */}
              <Card className="shadow-lg border-2 border-gray-100 bg-gradient-to-br bg-[#F7F7F7]">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-xl font-bold text-[#1F1F1F]">Why Get a Quote?</h3>
                  
                  <div className="space-y-3">
                    {[
                      'Free with no obligation',
                      'Compare multiple carriers',
                      'Expert guidance included',
                      'Fast 24-hour turnaround',
                      'Personalized recommendations',
                      'Save up to 40% on coverage'
                    ].map((benefit) => (
                      <div key={benefit} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quote;
